import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PieChart as RechartsPieChart,
  Pie,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import If from '@components/if/if.tsx';
import ChartLoader from '@shared/components/charts/chart-loader/chart-loader.tsx';
import ChartEmpty from '@shared/components/charts/chart-empty/chart-empty.tsx';
import { renderCustomizedLabel } from '@shared/components/charts/pie-chart/components/pie-chart-label/pie-chart-label.tsx';
import PieChartTooltip from '@shared/components/charts/pie-chart/components/pie-chart-tooltip/pie-chart-tooltip.tsx';
import {
  PieChartProps,
  PieChartLabelProps
} from '@shared/models/charts/pie-chart.model.ts';

const PieChart: React.FC<PieChartProps> = ({
  data,
  loading = false,
  config
}) => {
  const { t } = useTranslation();
  const {
    height = 280,
    innerRadius = 0,
    outerRadius = 100,
    showLabels = true,
    showCount = true,
    minLabelPercent = 0.1,
    valueFormatter,
    tooltipValueLabel
  } = config;

  // recharts reads a per-slice `fill` straight from the datum (see Pie.js
  // `entryWithCellInfo`), so mapping `color → fill` replaces the deprecated
  // <Cell> children while keeping identical colors.
  const pieData = data.map((item) => ({ ...item, fill: item.color }));

  return (
    <div
      className="bg-(--white) rounded-xl p-4 overflow-hidden relative"
      style={{
        boxShadow: '0 4px 12px var(--black-alpha-50)'
      }}
    >
      <If
        loading={loading}
        condition={!loading && data.length > 0}
        loadingContent={<ChartLoader height={height} />}
        emptyContent={<ChartEmpty height={height} />}
      >
        <div className="flex items-start" style={{ minHeight: height }}>
          <div className="flex flex-col gap-3 pr-6 shrink-0 mt-2.5">
            {data.map((item, index) => {
              const formattedValue = valueFormatter
                ? valueFormatter(item.value)
                : `$${item.value.toLocaleString()}`;

              return (
                <div
                  key={`legend-${index}`}
                  className="flex flex-col items-start gap-1 pl-2"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full shrink-0"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-[15px] font-normal text-(--blue-4)">
                      {item.name}
                    </span>
                  </div>
                  <div className="text-[12px] font-normal text-(--blue-4)">
                    {showCount
                      ? `${item.count} ${tooltipValueLabel ? t(tooltipValueLabel) : ''} · ${formattedValue}`
                      : formattedValue}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex-1">
            <ResponsiveContainer width="100%" height={height}>
              <RechartsPieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={innerRadius}
                  outerRadius={outerRadius}
                  dataKey="value"
                  labelLine={false}
                  stroke="var(--white)"
                  strokeWidth={2}
                  strokeLinejoin="round"
                  label={
                    showLabels
                      ? (props) =>
                          renderCustomizedLabel({
                            ...props,
                            data,
                            minLabelPercent,
                            showCount,
                            valueFormatter
                          } as PieChartLabelProps)
                      : undefined
                  }
                />
                <Tooltip
                  content={
                    <PieChartTooltip
                      showCount={showCount}
                      valueFormatter={valueFormatter}
                      tooltipValueLabel={tooltipValueLabel}
                      t={t}
                    />
                  }
                />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </If>
    </div>
  );
};

export default PieChart;
